import { Layer } from "../_overlay/Layer.js";
import { useToastClearance } from "../_overlay/toastClearance.js";
import { IconButton } from "../IconButton/IconButton.js";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/_dialog/DialogLayer.tsx
/**
* The shell shared by Modal and Drawer: a modal Layer whose root is the backdrop, and a
* `role="dialog"` panel with a header (title, description, close button), a body that
* scrolls (and takes focus when it does) and a footer. Render it only while open.
*/
function DialogLayer({ block, panelClassName, onClose, title, description, children, footer, dismissable, initialFocusRef, "aria-label": ariaLabel, closeLabel, container }) {
	const id = useId();
	const titleId = `${id}-title`;
	const descriptionId = `${id}-description`;
	const panelRef = useRef(null);
	const [body, setBody] = useState(null);
	const [bodyScrolls, setBodyScrolls] = useState(false);
	const [footerElement, setFooterElement] = useState(null);
	useToastClearance(footerElement);
	useLayoutEffect(() => {
		if (!body) return;
		const measure = () => setBodyScrolls(body.scrollHeight > body.clientHeight);
		measure();
		if (typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(measure);
		observer.observe(body);
		for (const child of Array.from(body.children)) observer.observe(child);
		return () => observer.disconnect();
	}, [body, children]);
	const hasTitle = title != null && title !== false;
	const hasDescription = description != null && description !== false;
	return /* @__PURE__ */ jsx(Layer, {
		kind: "modal",
		modal: true,
		contentRef: panelRef,
		...initialFocusRef && { initialFocusRef },
		...container !== void 0 && { container },
		dismissOnEscape: dismissable,
		dismissOnOutsidePress: dismissable,
		onDismiss: onClose,
		className: `${block}-backdrop`,
		onMouseDown: (event) => {
			if (event.target === event.currentTarget) event.preventDefault();
		},
		children: /* @__PURE__ */ jsxs("div", {
			ref: panelRef,
			role: "dialog",
			"aria-modal": "true",
			"aria-labelledby": hasTitle ? titleId : void 0,
			"aria-label": hasTitle ? void 0 : ariaLabel,
			"aria-describedby": hasDescription ? descriptionId : void 0,
			tabIndex: -1,
			className: panelClassName,
			children: [
				(hasTitle || hasDescription || dismissable) && /* @__PURE__ */ jsxs("div", {
					className: `${block}__header`,
					children: [/* @__PURE__ */ jsxs("div", {
						className: `${block}__heading`,
						children: [hasTitle && /* @__PURE__ */ jsx("h2", {
							id: titleId,
							className: `${block}__title`,
							children: title
						}), hasDescription && /* @__PURE__ */ jsx("p", {
							id: descriptionId,
							className: `${block}__description`,
							children: description
						})]
					}), dismissable && /* @__PURE__ */ jsx(IconButton, {
						icon: "x",
						size: "sm",
						"aria-label": closeLabel,
						className: `${block}__close`,
						onClick: onClose
					})]
				}),
				children != null && /* @__PURE__ */ jsx("div", {
					ref: setBody,
					className: `${block}__body`,
					tabIndex: bodyScrolls ? 0 : void 0,
					children
				}),
				footer != null && /* @__PURE__ */ jsx("div", {
					ref: setFooterElement,
					className: `${block}__footer`,
					children: footer
				})
			]
		})
	});
}
//#endregion
export { DialogLayer };

//# sourceMappingURL=DialogLayer.js.map